/**
 * PATH: packages/ui/src/kanban/components/card-detail/checklistDates.ts
 * FILE_ID: UI-KANBAN-CARDDETAIL-CHECKLISTDATES-001
 * PURPOSE: Pure formatter for a checklist action-item's own due date — the
 *          compact pill label + urgency tone shown on each checklist row.
 * WHY:     Operator (2026-06-23): "Dates for action items to link up to the 8
 *          week look ahead." Step 1 is letting each checklist step CARRY a
 *          date; this turns that ISO date into a glanceable, urgency-coloured
 *          label (Overdue / Today / Tue 24 Jun) consistent with the card
 *          due-pill vocabulary.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────────┐   ┌──────────────────────┐
 *   │ due (YMD/ISO)│ → │ local-calendar diff │ → │ {label, tone, days}  │
 *   └──────────────┘   └─────────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - ../../utils/localDate: THE canonical local-calendar helpers (never
 *     re-invent UTC date math — UAT P0 timezone bug).
 * RELATED:
 *   - card-detail/checklistItemRow.tsx: renders the pill from this.
 *   - components/card-detail/descriptionActions.ts (dueLabel sibling vocab).
 *
 * Pure I→O: (due, today) → display | null. No React, no network. Tested in
 * checklistDates.test.ts.
 */

import { diffCalendarDays, parseYmdLocal, todayLocalYmd } from '../../utils/localDate'

/** Urgency band for a checklist item's due date. */
export type ChecklistDueTone = 'overdue' | 'today' | 'soon' | 'future'

export interface ChecklistDueDisplay {
  /** Compact human label, e.g. "Overdue", "Today", "Tue 24 Jun". */
  label: string
  tone: ChecklistDueTone
  /** Signed whole-day diff from today (negative = in the past). */
  days: number
}

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MON_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * Turn a checklist item's `due_date` (YYYY-MM-DD, ISO datetime tolerated —
 * the date part wins) into a compact, urgency-toned pill descriptor. Returns
 * null when there is no date so callers can render the "set a date"
 * affordance instead.
 *
 * Tone bands: past → overdue, today → today, within a week → soon,
 * beyond → future. `complete` items can be passed as already-done so a
 * finished step never reads as "Overdue" (the caller decides; this stays
 * pure and just reports the calendar facts).
 */
export function formatChecklistDue(
  due: string | null | undefined,
  todayYmd: string = todayLocalYmd(),
): ChecklistDueDisplay | null {
  if (!due) return null
  const ymd = due.slice(0, 10)
  // Guard a malformed value (e.g. "") rather than emit "NaN" labels.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null
  const days = diffCalendarDays(ymd, todayYmd)
  const d = parseYmdLocal(ymd)
  const tone: ChecklistDueTone =
    days < 0 ? 'overdue' : days === 0 ? 'today' : days <= 7 ? 'soon' : 'future'
  let label: string
  if (days === 0) label = 'Today'
  else if (days === 1) label = 'Tomorrow'
  else if (days === -1) label = 'Yesterday'
  else label = `${WD_SHORT[d.getDay()]} ${d.getDate()} ${MON_SHORT[d.getMonth()]}`
  return { label, tone, days }
}

/** Tailwind classes per tone for the compact due pill (light + dark). */
export const CHECKLIST_DUE_TONE_CLASS: Record<ChecklistDueTone, string> = {
  overdue:
    'text-rose-700 bg-rose-50 ring-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:ring-rose-900/60',
  today:
    'text-amber-700 bg-amber-50 ring-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:ring-amber-900/60',
  soon:
    'text-blue-700 bg-blue-50 ring-blue-200 dark:text-blue-300 dark:bg-blue-950/40 dark:ring-blue-900/60',
  future:
    'text-neutral-600 bg-neutral-100 ring-neutral-200 dark:text-neutral-300 dark:bg-neutral-800/70 dark:ring-neutral-700/60',
}
